import React, { useContext, useEffect, useState } from 'react';
import { CatalogCarrierDetailOutDTO, EntityTypeEnum, PermissionTypesEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import StateBadge from '@components/ringil3/Badges/StateBadge.tsx';
import CrnBadge from '@components/ringil3/Badges/CrnBadge.tsx';
import AddressBadge from '@components/ringil3/Badges/AddressBadge.tsx';
import OriginalNameBadge from '@components/ringil3/Badges/OriginalNameBadge.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgContacts from '@icons/id.svg?react';
import SvgInfo from '@icons/info.svg?react';
import CarrierDetailAbout from '@app/pages/customer/carriers/carrier-detail/CarrierDetailAbout.tsx';
import CarrierDetailContacts from '@app/pages/customer/carriers/carrier-detail/CarrierDetailContacts.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  carrierId: string;
  carrierReferrer?: CarrierReferrer;
}

export enum CarrierReferrer {
  CARRIERS = 'carriers',
  CARRIER_CATALOG = 'carrier-catalog',
  PARTNERS = 'partners',
}

const CarrierDetail: React.FC<ComponentProps> = ({ carrierId, carrierReferrer = CarrierReferrer.CARRIERS }) => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);

  const [activeTab, setActiveTab] = useState(0);

  const {
    cuDetailCarrier,
    cuAddCatalogCarrierToMyCompany,
    cuDeleteCarrier,
  } = useContext(ApiContext);
  const [carrierData, setCarrierData] = useState<CatalogCarrierDetailOutDTO>(undefined);

  const navigation = useNavigation();

  const loadCarrierData = () => {
    cuDetailCarrier(carrierId, data => {
      setCarrierData(data);
    });
  };

  const load = () => {
    loadCarrierData();
  };

  useEffect(() => {
    load();
  }, []);

  const carrierBreadCrumb = (name: string) => {
    return [
      {
        label: tr(`AllCarriersList.titleCarriers`, `All carriers`),
        url: navigation.createNavigationLink(navigation.urlFunctions.createMyCarrierList()),
      },
      {
        label: name ? name : tr('CarrierDetail.carrierDetail', 'Carrier detail'),
        disabled: true,
      },
    ];
  };

  const carrierCatalogBreadCrumb = (name: string) => {
    return [
      {
        label: tr('CarrierDetail.carrierCatalog', 'Carrier catalog'),
        url: /*"/customer/carrier-catalog"*/ navigation.createNavigationLink(navigation.urlFunctions.createCarrierCatalogList()),
      },
      {
        label: name ? name : tr('CarrierDetail.carrierDetail', 'Carrier detail'),
        disabled: true,
      },
    ];
  };

  const partnersBreadCrumb = (name: string) => {
    return [
      {
        label: tr(`Partners.breadCrumb`, `Partners`),
        url: navigation.createNavigationLink(navigation.urlFunctions.partnersUrlInfo('2')),
      },
      {
        label: name ? name : tr('CarrierDetail.carrierDetail', 'Carrier detail'),
        disabled: true,
      },
    ];
  };

  const editCarrierEnabled = hasPermission([PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW]);

  const addCarrierToMyCompany = (carrierId: string) => {
    cuAddCatalogCarrierToMyCompany(carrierId, () => {
      load();
    });
  };

  const removeCarrierFromMyCompany = (carrierId: string) => {
    cuDeleteCarrier(carrierId, () => {
      navigation.navigate(navigation.urlFunctions.createMyCarrierList());
    });
  };

  const tabsConfig: TabProps[] = [
    {
      label: tr(`CarrierDetail.tabAbout`, `About company`),
      active: activeTab === 0,
      icon: <SvgInfo />,
    },
    {
      label: tr(`CarrierDetail.tabContacts`, `Contact persons`),
      active: activeTab === 1,
      icon: <SvgContacts />,
    },
    // {
    //   label: tr(`CarrierDetail.tabRating`, `Rating`),
    //   active: activeTab === 2,
    //   icon: <SvgStar />,
    // },
  ];

  const breadcrumbItems = (() => {
    switch (carrierReferrer) {
      case CarrierReferrer.CARRIERS:
        return carrierBreadCrumb(carrierData?.name);
      case CarrierReferrer.CARRIER_CATALOG:
        return carrierCatalogBreadCrumb(carrierData?.name);
      case CarrierReferrer.PARTNERS:
        return partnersBreadCrumb(carrierData?.name);
      default:
        return carrierBreadCrumb(carrierData?.name);
    }
  })();

  if (!carrierData) return <></>;

  return (
    <>
      <BreadCrumbComponent items={breadcrumbItems} />

      <Heading
        title={carrierData.name}
        headingVariant={'h1'}
        labels={
          <LabelsTag objectId={carrierData.carrierId} objectType={EntityTypeEnum.CARRIER_COMPANY}
                     resolveLabelsForType={EntityTypeEnum.CARRIER_COMPANY} disabled={!editCarrierEnabled} />
        }
        buttons={
          editCarrierEnabled
            ? [
              carrierData.isAlreadyAdded ? (
                <RingilButton
                  label={tr('CarrierDetail.removeCarrier', 'Odebrat z mých dopravců')}
                  onClick={() => removeCarrierFromMyCompany(carrierData.carrierId)}
                />
              ) : (
                <RingilButton
                  label={tr('CarrierDetail.addCarrier', 'Přidat mezi své dopravce')}
                  onClick={() => addCarrierToMyCompany(carrierData.carrierId)}
                />
              ),
            ]
            : []
        }
      />
      <Ringil3BadgeRow>
        <StateBadge
          state={carrierData.isRegistered ? tr('CarrierDetail.stateRegistered', 'Registered') : tr('CarrierDetail.stateNotRegistered', 'Not Registered')} />
        <CrnBadge crn={carrierData.crn} />
        <AddressBadge
          country={carrierData.country}
          zipCode={carrierData.zipCode}
          city={carrierData.city}
          streetNr={carrierData.streetNr}
          timezoneId={carrierData.timezoneId}
        />
        {carrierData.originalName && carrierData.originalName !== carrierData.name && (
          <OriginalNameBadge originalName={carrierData.originalName} />
        )}
      </Ringil3BadgeRow>


      <Tabs
        tabs={tabsConfig}
        onTabChange={setActiveTab}
        replaceUrl
      />

      {activeTab === 0 && <CarrierDetailAbout carrierData={carrierData} reloadData={loadCarrierData} />}
      {activeTab === 1 && <CarrierDetailContacts carrierData={carrierData} refreshData={loadCarrierData} />}
    </>
  );
};

export default CarrierDetail;
